import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, inr, today } from "@/lib/api";

const monthLabel = (m) => new Date(`${m}-01`).toLocaleString("en-IN", { month: "short", year: "2-digit" });

function Line({ k, v, cls = "", testid }) {
  return <div className="flex justify-between py-1 text-sm"><span className="text-muted-foreground">{k}</span><span className={`amount ${cls}`} data-testid={testid}>{v}</span></div>;
}

export function VehicleProfitDialog({ v, onClose }) {
  const [month, setMonth] = useState(today().slice(0, 7));
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!v) return;
    setD(null);
    api.get(`/central-expenses/vehicle-profit/${v.id}`, { params: { month } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [v, month]);
  const c = d?.current;
  return (
    <Dialog open={!!v} onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto bg-card" data-testid="vehicle-profit-dialog">
        <DialogHeader><DialogTitle className="flex flex-wrap items-center gap-3"><span className="font-mono">{v?.vehicle_number}</span> Profit Card
          <Input type="month" className="ml-auto h-8 w-40" data-testid="vp-month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} /></DialogTitle></DialogHeader>
        {!c ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-xl border p-3"><div className="label-xs">Earnings</div><div className="amount mt-1 text-lg font-bold text-emerald-600" data-testid="vp-revenue">{inr(c.revenue - c.brokerage)}</div><div className="text-[11px] text-muted-foreground">{c.trips} trips</div></div>
              <div className="rounded-xl border p-3"><div className="label-xs">Total Costs</div><div className="amount mt-1 text-lg font-bold text-rose-600" data-testid="vp-costs">{inr(c.total_cost)}</div></div>
              <div className={`rounded-xl border p-3 ${c.net >= 0 ? "bg-emerald-50 dark:bg-emerald-950/30" : "bg-rose-50 dark:bg-rose-950/30"}`}><div className="label-xs">Net Profit</div><div className={`amount mt-1 text-lg font-bold ${c.net >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-rose-400"}`} data-testid="vp-net">{inr(c.net)}</div></div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl border p-4">
                <div className="label-xs mb-2">Earnings</div>
                <Line k="Freight (own LRs)" v={inr(c.revenue, 2)} />
                {c.brokerage > 0 && <Line k="Less: Brokerage" v={`− ${inr(c.brokerage, 2)}`} cls="text-rose-600" />}
              </div>
              <div className="rounded-xl border p-4" data-testid="vp-cost-breakdown">
                <div className="label-xs mb-2">Costs</div>
                {c.costs.map((x) => <Line key={x.key} k={x.label} v={inr(x.amount, 2)} testid={`vp-cost-${x.key}`} />)}
                {c.driver_bills > 0 && <Line k="Driver trip bills" v={inr(c.driver_bills, 2)} testid="vp-driver-bills" />}
                {c.driver_cost > 0 && <Line k="Driver salary + KM" v={inr(c.driver_cost, 2)} testid="vp-driver-cost" />}
                {!c.costs.length && !c.driver_bills && !c.driver_cost && <p className="text-sm text-muted-foreground">No costs this month</p>}
              </div>
            </div>
            <div className="h-52 rounded-xl border p-3" data-testid="vp-history-chart">
              <div className="label-xs mb-1">Net profit — last 6 months</div>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={d.history.map((h) => ({ ...h, label: monthLabel(h.month) }))}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                  <XAxis dataKey="label" fontSize={11} />
                  <YAxis fontSize={11} width={55} tickFormatter={(x) => `${Math.round(x / 1000)}k`} />
                  <Tooltip formatter={(x) => inr(x)} />
                  <Bar dataKey="net" radius={[4, 4, 0, 0]}>{d.history.map((h) => <Cell key={h.month} fill={h.net >= 0 ? "#10b981" : "#ef4444"} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
